import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Crown, LogOut, Home, CreditCard, Settings, 
  ExternalLink, Copy, CheckCircle, AlertCircle, 
  Clock, XCircle, RefreshCw, Users, DollarSign, Share2, Menu
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const [subscription, setSubscription] = useState(null);
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedReferral, setCopiedReferral] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [subRes, refRes] = await Promise.all([
        axios.get(`${API}/subscriptions/my`),
        axios.get(`${API}/referrals/earnings`)
      ]);
      setSubscription(subRes.data);
      setReferralData(refRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) {
      return;
    }
    
    setCancelling(true);
    try {
      await axios.post(`${API}/subscriptions/${subscription.id}/cancel`);
      toast.success('Subscription cancelled');
      setSubscription(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const handleVerifySubscription = async () => {
    if (!subscription?.paypal_subscription_id) return;
    
    setVerifying(true);
    try {
      const response = await axios.post(`${API}/subscriptions/verify/${subscription.paypal_subscription_id}`);
      setSubscription(response.data);
      if (response.data.status === 'ACTIVE') {
        toast.success('Subscription verified and activated!');
      } else {
        toast.info(`Subscription status: ${response.data.status}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify subscription');
    } finally {
      setVerifying(false);
    }
  };

  const copyInviteLink = () => {
    if (subscription?.telegram_invite_link) {
      navigator.clipboard.writeText(subscription.telegram_invite_link);
      setCopied(true);
      toast.success('Invite link copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const copyReferralLink = () => {
    if (user?.referral_code) {
      const referralLink = `${window.location.origin}/register?ref=${user.referral_code}`;
      navigator.clipboard.writeText(referralLink);
      setCopiedReferral(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopiedReferral(false), 2000);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'ACTIVE':
        return <span className="badge badge-green"><CheckCircle className="w-3 h-3 mr-1" /> Active</span>;
      case 'PENDING':
        return <span className="badge badge-gold"><Clock className="w-3 h-3 mr-1" /> Pending</span>;
      case 'PENDING_APPROVAL':
        return <span className="badge badge-gold"><Clock className="w-3 h-3 mr-1" /> Awaiting Approval</span>;
      case 'CANCELLED':
        return <span className="badge badge-red"><XCircle className="w-3 h-3 mr-1" /> Cancelled</span>;
      case 'EXPIRED':
        return <span className="badge badge-gray"><AlertCircle className="w-3 h-3 mr-1" /> Expired</span>;
      default:
        return <span className="badge badge-gray">{status}</span>;
    }
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#050505] border-b border-white/5 px-4 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <Crown className="w-6 h-6 text-gold-400" />
          <span className="font-heading text-base font-bold text-white">House of Royalty</span>
        </Link>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="text-white p-2"
          aria-label="Toggle menu"
        >
          {sidebarOpen ? <XCircle className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 border-r border-white/5 flex flex-col bg-[#050505]
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-6 border-b border-white/5 hidden lg:block">
          <Link to="/" className="flex items-center gap-3">
            <Crown className="w-8 h-8 text-gold-400" />
            <span className="font-heading text-lg font-bold text-white">House of Royalty</span>
          </Link>
        </div>
        
        <nav className="flex-1 py-6 mt-14 lg:mt-0">
          <Link to="/dashboard" className="sidebar-link active" onClick={() => setSidebarOpen(false)}>
            <Home className="w-5 h-5" />
            Dashboard
          </Link>
          <Link to="/pricing" className="sidebar-link" onClick={() => setSidebarOpen(false)}>
            <CreditCard className="w-5 h-5" />
            Membership
          </Link>
          {user?.role === 'admin' && (
            <Link to="/admin" className="sidebar-link" onClick={() => setSidebarOpen(false)}>
              <Settings className="w-5 h-5" />
              Admin Panel
            </Link>
          )}
        </nav>
        
        <div className="p-6 border-t border-white/5">
          <div className="mb-4">
            <p className="text-white font-medium truncate">{user?.name}</p>
            <p className="text-zinc-500 text-sm truncate">{user?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors text-sm"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-4 lg:p-8 overflow-auto pt-16 lg:pt-8">
        <div className="max-w-4xl">
          <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white mb-2">Welcome back, {user?.name}</h1>
          <p className="text-zinc-500 mb-8">Manage your membership and access your exclusive benefits.</p>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-gold-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Referral Program Section */}
              <div className="glass-heavy p-6" data-testid="referral-section">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-gold-500/20 flex items-center justify-center">
                    <Share2 className="w-5 h-5 text-gold-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-heading font-semibold text-white">Referral Program</h2>
                    <p className="text-zinc-500 text-sm">Earn 10% commission on every subscription</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-black/30 p-4 border border-white/5">
                    <p className="text-zinc-500 text-sm mb-1">Your Referral Code</p>
                    <p className="text-gold-400 font-mono text-lg font-bold">{user?.referral_code || 'N/A'}</p>
                  </div>
                  <div className="bg-black/30 p-4 border border-white/5">
                    <p className="text-zinc-500 text-sm mb-1">Active Referrals</p>
                    <p className="text-white text-lg font-bold">{referralData?.active_referrals || 0}</p>
                  </div>
                  <div className="bg-black/30 p-4 border border-white/5">
                    <p className="text-zinc-500 text-sm mb-1">Total Earnings</p>
                    <p className="text-green-400 text-lg font-bold">${referralData?.total_earned?.toFixed(2) || '0.00'}</p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={copyReferralLink}
                    className="btn-gold flex items-center justify-center gap-2"
                    data-testid="copy-referral-link-btn"
                  >
                    {copiedReferral ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copiedReferral ? 'Copied!' : 'Copy Referral Link'}
                  </button>
                  <div className="flex-1 bg-black/30 border border-white/10 px-4 py-2 flex items-center">
                    <span className="text-zinc-500 text-sm truncate">
                      {window.location.origin}/register?ref={user?.referral_code}
                    </span>
                  </div>
                </div>

                {referralData?.commissions?.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-white/10">
                    <h3 className="text-white font-medium mb-4">Recent Commissions</h3>
                    <div className="space-y-3">
                      {referralData.commissions.slice(0, 5).map((commission) => (
                        <div key={commission.id} className="flex items-center justify-between py-2 border-b border-white/5">
                          <div>
                            <p className="text-white text-sm">{commission.referred_user?.name || 'User'}</p>
                            <p className="text-zinc-500 text-xs">{commission.tier_name} - {new Date(commission.created_at).toLocaleDateString()}</p>
                          </div>
                          <p className="text-green-400 font-medium">+${commission.commission_amount.toFixed(2)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Subscription Status */}
              {subscription && ['ACTIVE', 'PENDING', 'PENDING_APPROVAL'].includes(subscription.status) ? (
                <>
                  <div className="glass-heavy p-6" data-testid="subscription-status">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-heading font-semibold text-white">Your Membership</h2>
                      {getStatusBadge(subscription.status)}
                    </div>
                    
                    <div className="grid md:grid-cols-3 gap-6 mb-6">
                      <div>
                        <p className="text-zinc-500 text-sm mb-1">Tier</p>
                        <p className="text-white font-medium">{subscription.tier_name}</p>
                      </div>
                      <div>
                        <p className="text-zinc-500 text-sm mb-1">Started</p>
                        <p className="text-white font-medium">
                          {subscription.started_at ? new Date(subscription.started_at).toLocaleDateString() : 'Pending'}
                        </p>
                      </div>
                      <div>
                        <p className="text-zinc-500 text-sm mb-1">Renews</p>
                        <p className="text-white font-medium">
                          {subscription.expires_at ? new Date(subscription.expires_at).toLocaleDateString() : 'Pending'}
                        </p>
                      </div>
                    </div>

                    {subscription.status === 'PENDING' && (
                      <button
                        onClick={handleVerifySubscription}
                        disabled={verifying}
                        className="btn-outline flex items-center gap-2"
                        data-testid="verify-subscription-btn"
                      >
                        <RefreshCw className={`w-4 h-4 ${verifying ? 'animate-spin' : ''}`} />
                        {verifying ? 'Verifying...' : 'Verify Payment'}
                      </button>
                    )}

                    {subscription.status === 'PENDING_APPROVAL' && (
                      <div className="p-4 bg-amber-500/10 border border-amber-500/30">
                        <p className="text-amber-400 font-medium mb-1">Awaiting Admin Approval</p>
                        <p className="text-zinc-400 text-sm">
                          Thank you for subscribing! Your membership requires admin approval. 
                          You&apos;ll receive access once approved.
                        </p>
                      </div>
                    )}

                    {subscription.status === 'ACTIVE' && (
                      <button
                        onClick={handleCancelSubscription}
                        disabled={cancelling}
                        className="text-red-500 hover:text-red-400 transition-colors text-sm"
                        data-testid="cancel-subscription-btn"
                      >
                        {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
                      </button>
                    )}
                  </div>

                  {/* Telegram Access */}
                  {subscription.status === 'ACTIVE' && subscription.telegram_invite_link && (
                    <div className="glass-heavy p-6" data-testid="telegram-access">
                      <h2 className="text-xl font-heading font-semibold text-white mb-4">Telegram Access</h2>
                      <p className="text-zinc-400 text-sm mb-6">
                        Use the link below to join our exclusive Telegram community. This is your personal invite link - do not share it with others.
                      </p>
                      
                      <div className="flex items-center gap-4">
                        <a
                          href={subscription.telegram_invite_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-gold flex items-center gap-2"
                          data-testid="join-telegram-btn"
                        >
                          Join Telegram Group
                          <ExternalLink className="w-4 h-4" />
                        </a>
                        <button
                          onClick={copyInviteLink}
                          className="btn-outline flex items-center gap-2"
                          data-testid="copy-invite-link-btn"
                        >
                          {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          {copied ? 'Copied!' : 'Copy Link'}
                        </button>
                      </div>
                    </div>
                  )}

                  {subscription.status === 'ACTIVE' && !subscription.telegram_invite_link && (
                    <div className="glass-heavy p-6">
                      <h2 className="text-xl font-heading font-semibold text-white mb-4">Telegram Access</h2>
                      <p className="text-zinc-400 text-sm">
                        Your invite link is being generated. Please refresh the page in a few moments.
                      </p>
                    </div>
                  )}
                </>
              ) : subscription && ['EXPIRED', 'CANCELLED', 'UNKNOWN'].includes(subscription.status) ? (
                <div className="glass-heavy p-8" data-testid="expired-subscription">
                  <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                    <h2 className="text-xl font-heading font-semibold text-white">Welcome Back</h2>
                    {getStatusBadge(subscription.status)}
                  </div>
                  <p className="text-zinc-400 mb-6">
                    Your <span className="text-white font-medium">{subscription.tier_name}</span> membership{' '}
                    {subscription.status === 'EXPIRED' ? 'expired' : 'ended'}
                    {subscription.expires_at ? ` on ${new Date(subscription.expires_at).toLocaleDateString()}` : ''}.
                    Renew now to regain access to the exclusive Telegram community.
                  </p>
                  <Link
                    to="/pricing"
                    className="btn-gold inline-flex items-center gap-2"
                    data-testid="renew-membership-btn"
                  >
                    <Crown className="w-4 h-4" />
                    Renew Membership
                  </Link>
                </div>
              ) : (
                <div className="glass-heavy p-8 text-center" data-testid="no-subscription">
                  <Crown className="w-16 h-16 text-gold-400 mx-auto mb-6" />
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">No Active Membership</h2>
                  <p className="text-zinc-400 mb-8 max-w-md mx-auto">
                    Join our exclusive community and unlock premium benefits. Choose a membership tier to get started.
                  </p>
                  <Link to="/pricing" className="btn-gold inline-flex items-center gap-2">
                    View Membership Tiers
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
